import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {StyledInvoiceTabContainer} from './StyledInvoicesTab';
import TableFilters from '../../tabComponents/TableFilters';
import NoSearchResults from '../../../../NoSearchResults';
import Table from '../../../../Table';
import {amountHelpers, dateHelpers, invoicesHelpers} from '../../../../../utils/helpers';
import {useEmptyDetails, useFeatureAvailable} from '../../../../../hooks';
import {companyFeaturesConstants} from '../../../../../constants';

const dateColumnProps = {
  render: (value) => <span className='no-wrap'>{value ? dateHelpers.getDateWithMonth(value) : ''}</span>,
  sortDirections: ['descend', 'ascend'],
  sorter: true,
  width: 120,
};

const InvoicesTab = ({
  data = [],
  empty,
  loading = false,
  onFilter,
  tableHeaderContent,
  tableFiltersProps,
  tableProps,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'invoices']);
  const [query, setQuery] = useState({
    search: ''
  });

  const { emptyDetails, isEnabledEmpty } = useEmptyDetails({ data, empty, loading, query });

  const isBudgetsAvailable = useFeatureAvailable(companyFeaturesConstants.BUDGETS_PO_AVAILABLE);

  const isBankingDisabled = useFeatureAvailable(companyFeaturesConstants.TREEZOR_DISABLED);

  const handleOnFilter = (filterParams) => {
    setQuery({...query, ...filterParams});
    onFilter?.(filterParams);
  }

  const getColumns = () => {
    return [
      {
        dataIndex: 'invoice_number',
        title: `${t('invoice')} ${t('number')}`,
        render: (value) => value ?? '',
      },
      {
        dataIndex: 'date',
        title: `${t('invoice')} ${t('date')}`,
        ...dateColumnProps
      },
      {
        dataIndex: 'vendor_local_name',
        render: (_, record) => invoicesHelpers.getInvoiceVendorName(record),
        title: t('vendor')
      },
      {
        dataIndex: 'due_date',
        title: `${t('due')} ${t('date')}`,
        ...dateColumnProps
      },
      {
        dataIndex: 'paid_date',
        title: `${t('payment')} ${t('date')}`,
        ...dateColumnProps
      },
      {
        align: 'right',
        dataIndex: 'amount',
        render: (amount, record) => (
          <span className='amount'>
            {amount ? amountHelpers.getAmountWithCurrencyCode(amount, undefined, record?.currency) : ''}
          </span>
        ),
        sortDirections: ['descend', 'ascend'],
        sorter: true,
        title: t('amount'),
      },
      (isBudgetsAvailable && isBankingDisabled) && {
        dataIndex: 'approval_status',
        render: (_, record) => invoicesHelpers.getInvoiceApprovalStatusTag(record, t),
        title: `${t('approval')} ${t('status')}`
      },
      {
        dataIndex: 'payment_status',
        render: invoicesHelpers.getInvoiceStatusMark,
        sorter: true,
        title: t('status')
      },
      {
        align: 'right',
        dataIndex: 'source_type',
        render: invoicesHelpers.getInvoiceSourceTypeTag,
      },
      {
        dataIndex: 'match',
        render: (_, record) => invoicesHelpers.getInvoiceMatchType(record),
        title: '',
        width: 64
      }
    ].filter(Boolean);
  }

  const columns = getColumns();

  return (
    <StyledInvoiceTabContainer
      {...rest}
      size='large'
      direction='vertical'
    >
      <TableFilters
        {...tableFiltersProps}
        onFilter={handleOnFilter}
      />
      {isEnabledEmpty ? empty : (
        <>
          {emptyDetails.enabled ? (
            <NoSearchResults
              spinning={loading}
              value={emptyDetails.text}
            />
          ) : (
            <>
              {tableHeaderContent}
              <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                rowKey='id'
                {...tableProps}
              />
            </>
          )}
        </>
      )}
    </StyledInvoiceTabContainer>
  );
}

InvoicesTab.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  empty: PropTypes.any,
  loading: PropTypes.bool,
  unmatched: PropTypes.bool,
  onFilter: PropTypes.func,
  tableFiltersProps: PropTypes.any,
  tableHeaderContent: PropTypes.any,
  tableProps: PropTypes.object
}

export default InvoicesTab;
